import type { TradeOrderStatusVersion } from '@festina/unitlink-general-types/unitlink'
import { translationUtils } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import {
  createColumnDefiner,
  SortableTable,
} from '@festina-life/ui/listsAndTables'
import { useMemo } from 'react'

type Props = {
  tradeOrderStatuses: TradeOrderStatusVersion[]
}

const defineColumn = createColumnDefiner<TradeOrderStatusVersion>()

export const TradeOrderStatusTable = ({ tradeOrderStatuses }: Props) => {
  const translate = useTranslate()

  const columns = useMemo(
    () => ({
      acknowledged: defineColumn({
        heading: translate('tradeCycle.tradeOrderStatus.acknowledged'),
        keyPath: 'acknowledged',
        formatter: (x) => translationUtils.translateBoolean(x, translate),
      }),
      allPricesReceived: defineColumn({
        heading: translate('tradeCycle.tradeOrderStatus.allPricesReceived'),
        keyPath: 'allPricesReceived',
        formatter: (x) => translationUtils.translateBoolean(x, translate),
      }),
      assetManagerId: defineColumn({
        heading: translate('tradeCycle.tradeOrderStatus.assetManagerId'),
        keyPath: 'assetManagerId',
      }),
      fundPriceStatuses: defineColumn({
        heading: translate('tradeCycle.tradeOrderStatus.fundPriceStatuses'),
        keyPath: 'fundPriceStatuses',
        formatter: (statuses) => (
          <>
            {statuses?.map((status, index) => (
              // oxlint-disable-next-line react/no-array-index-key -- we don't have a unique key
              <div key={index}>
                {status?.received
                  ? translate(
                      'tradeCycle.tradeOrderStatus.fundPriceStatus.received',
                      {
                        isin: status.isin ?? '-',
                      },
                    )
                  : translate(
                      'tradeCycle.tradeOrderStatus.fundPriceStatus.notReceived',
                      {
                        isin: status?.isin ?? '-',
                      },
                    )}
              </div>
            ))}
          </>
        ),
        sortable: false,
      }),
      orderId: defineColumn({
        heading: translate('tradeCycle.tradeOrderStatus.orderId'),
        keyPath: 'orderId',
      }),
      sent: defineColumn({
        heading: translate('tradeCycle.tradeOrderStatus.sent'),
        keyPath: 'sent',
        formatter: (x) => translationUtils.translateBoolean(x, translate),
      }),
    }),
    [translate],
  )

  return <SortableTable data={tradeOrderStatuses} columns={columns} />
}
